// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { CareTeamNote } from './useCareTeamNotes';

const STORAGE_KEY_PREFIX = 'polaris.dashboard.care-team-notes-last-seen.';

/**
 * Subscribe to localStorage updates for the patient-scoped "last seen note"
 * timestamp. Wrapping with useSyncExternalStore lets the banner count + table
 * refresh in the same render after a note is opened.
 */
function subscribe(callback: () => void): () => void {
  const handler = (event: StorageEvent): void => {
    if (event.key?.startsWith(STORAGE_KEY_PREFIX)) {
      callback();
    }
  };
  window.addEventListener('storage', handler);
  // Storage events don't fire in the originating tab, so we also listen for
  // the custom event we dispatch from `markAllAsSeen`.
  window.addEventListener('polaris:care-team-notes-seen', callback);
  return () => {
    window.removeEventListener('storage', handler);
    window.removeEventListener('polaris:care-team-notes-seen', callback);
  };
}

function readLastSeen(patientId: string | undefined): number {
  if (!patientId || typeof window === 'undefined') {
    return 0;
  }
  const raw = window.localStorage.getItem(`${STORAGE_KEY_PREFIX}${patientId}`);
  const parsed = raw ? Number(raw) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

export interface UseUnreadCareTeamNotesReturn {
  unreadCount: number;
  /** Marks the current latest note timestamp as seen, dropping `unreadCount` to 0. */
  markAllAsSeen: () => void;
}

/**
 * Tracks how many care-team notes are newer than the last `lastUpdated`
 * timestamp the patient has acknowledged on this device. The acknowledgement
 * is bumped only when the patient opens the dashboard's note modal (or
 * dismisses the banner).
 */
export function useUnreadCareTeamNotes(
  notes: CareTeamNote[],
  patientId: string | undefined
): UseUnreadCareTeamNotesReturn {
  const lastSeen = useSyncExternalStore(
    subscribe,
    () => readLastSeen(patientId),
    () => 0
  );

  const latestNoteTime = useMemo(() => {
    let max = 0;
    for (const note of notes) {
      const t = note.observation.meta?.lastUpdated ? Date.parse(note.observation.meta.lastUpdated) : 0;
      if (Number.isFinite(t) && t > max) {
        max = t;
      }
    }
    return max;
  }, [notes]);

  const unreadCount = useMemo(() => {
    if (!patientId || latestNoteTime === 0) {
      return 0;
    }
    return notes.filter((note) => {
      const t = note.observation.meta?.lastUpdated ? Date.parse(note.observation.meta.lastUpdated) : 0;
      return Number.isFinite(t) && t > lastSeen;
    }).length;
  }, [notes, lastSeen, latestNoteTime, patientId]);

  const markAllAsSeen = useCallback(() => {
    if (!patientId || typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(`${STORAGE_KEY_PREFIX}${patientId}`, String(Date.now()));
    window.dispatchEvent(new Event('polaris:care-team-notes-seen'));
  }, [patientId]);

  return { unreadCount, markAllAsSeen };
}
